import { usePageTitle } from "@/hooks/use-page-title";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ResponsiveLayout } from "@/components/layout/responsive-layout";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, startOfDay } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  FileText, 
  Eye, 
  Mail, 
  AlertTriangle, 
  CreditCard, 
  Receipt,
  Loader2,
  CheckCircle2,
  ChevronRight,
  User,
  Clock,
  X,
  Undo2,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ActionItemType = 'work_order' | 'watchlist' | 'authority_letter' | 'fmcsa_incident' | 'payment' | 'invoice';

interface ActionItemStatus {
  id: number;
  userId: string;
  itemType: ActionItemType;
  itemId: number;
  status: 'snoozed' | 'dismissed';
  snoozedUntil: string | null;
  dismissReason: string | null;
  createdAt: string;
}

interface DashboardItem {
  id: number;
  isSnoozed?: boolean;
  snoozedUntil?: string;
}

interface DashboardData {
  openWorkOrders: Array<DashboardItem & {
    workOrderNumber: string;
    status: string;
    serviceType: string;
    serviceTypeName?: string | null;
    companyName: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
    latestStatusChangedAt?: string | null;
  }>;
  openWorkOrderCounts?: { internal: number; client: number; agency: number };
  totalCounts?: {
    openWorkOrders: number;
    expiredWatchlists: number;
    unsentLetters: number;
    unresolvedIncidents: number;
    failedPayments: number;
    unpaidInvoices: number;
    agedWatchlists: number;
  };
  expiredWatchlists: Array<DashboardItem & {
    helixWatchId: number;
    targetType: string;
    targetIdentifier: string;
    targetLabel: string | null;
    workOrderId: number;
    workOrderNumber: string | null;
    companyName: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
  }>;
  unsentLetters: Array<DashboardItem & {
    letterType: string | null;
    docketFull: string | null;
    companyName: string | null;
    emailStatus: string | null;
    serviceDate: string | null;
    workOrderId: number;
    workOrderNumber: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
  }>;
  unresolvedIncidents: Array<DashboardItem & {
    incidentNumber: string;
    status: string;
    notes: string | null;
    companyId: number | null;
    companyName: string | null;
    createdBy: string | null;
    createdByFirstName: string | null;
    createdByLastName: string | null;
    createdAt: string;
  }>;
  failedPayments: Array<DashboardItem & {
    workOrderId: number;
    stripeInvoiceNumber: string | null;
    status: string;
    amountDue: string | null;
    workOrderNumber: string | null;
    companyName: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
  }>;
  unpaidInvoices: Array<DashboardItem & {
    workOrderId: number;
    stripeInvoiceNumber: string | null;
    hostedInvoiceUrl: string | null;
    status: string;
    amountDue: string | null;
    unpaidBalance: string | null;
    workOrderNumber: string | null;
    companyName: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
  }>;
  agedWatchlists: Array<DashboardItem & {
    helixWatchId: number;
    targetType: string;
    targetIdentifier: string;
    targetLabel: string | null;
    watchlistType: string;
    watchlistDays: number | null;
    letterType: string | null;
    workOrderId: number;
    workOrderNumber: string | null;
    companyName: string | null;
    assignedToUserId: string | null;
    assignedFirstName: string | null;
    assignedLastName: string | null;
    createdAt: string;
  }>;
  isAdmin: boolean;
  currentUserId: string;
  actionItemStatuses: ActionItemStatus[];
}

const statusColors: Record<string, string> = {
  // Work order statuses — actor-grouped color scheme
  // Client-action (amber): client needs to act
  collecting_inputs: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  awaiting_payment: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  awaiting_signatures: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  awaiting_client_filing: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  // Internal (blue): DNA staff working
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  validating: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  preparing_documents: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  work_complete: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  // External (purple): submitted to third parties
  submitted: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  // Terminal (gray)
  closed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  cancelled: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  // Legacy work order statuses
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  awaiting_documents: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  // Payment/invoice statuses
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  blocked_test_mode: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
  scheduled: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300",
  overdue: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  partial: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  uncollectible: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  voided: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};

function formatAssignee(firstName: string | null, lastName: string | null): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) return firstName;
  if (lastName) return lastName;
  return "Unassigned";
}

function formatCurrency(amount: string | null): string {
  if (!amount) return "$0.00";
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD' 
  }).format(parseFloat(amount));
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatSnoozedUntil(snoozedUntil: string): string {
  const date = new Date(snoozedUntil);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  // Show relative time for short durations, date for longer ones
  if (diffHours <= 0) {
    return "now";
  } else if (diffHours < 24) {
    return `${diffHours}h (${format(date, 'h:mm a')})`;
  } else if (diffDays < 7) {
    return `${diffDays}d (${format(date, 'EEE h:mm a')})`;
  } else {
    return format(date, 'MMM d, h:mm a');
  }
}

interface SnoozeDropdownProps {
  onSnooze: (snoozedUntil: Date) => void;
  onUnsnooze?: () => void;
  isSnoozed?: boolean;
  snoozedUntil?: string;
  disabled?: boolean;
}

function SnoozeDropdown({ onSnooze, onUnsnooze, isSnoozed, snoozedUntil, disabled }: SnoozeDropdownProps) {
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [customTime, setCustomTime] = useState("09:00");

  const snoozeOptions = [
    { label: "4 hours", getDate: () => new Date(Date.now() + 4 * 60 * 60 * 1000) },
    { label: "1 day", getDate: () => new Date(Date.now() + 24 * 60 * 60 * 1000) },
    { label: "3 days", getDate: () => new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) },
    { label: "1 week", getDate: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
  ];

  const handleCustomSnooze = () => {
    if (customDate) {
      const [hours, minutes] = customTime.split(':').map(Number);
      const snoozeDate = new Date(customDate);
      snoozeDate.setHours(hours, minutes, 0, 0);
      if (snoozeDate > new Date()) {
        onSnooze(snoozeDate);
        setCustomDateOpen(false);
        setCustomDate(undefined);
        setCustomTime("09:00");
      }
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={cn("h-7 w-7 p-0", isSnoozed && "text-amber-600")}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            data-testid="btn-snooze"
          >
            <Clock className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {isSnoozed && onUnsnooze && (
            <>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onUnsnooze();
                }}
                className="text-amber-600"
                data-testid="snooze-cancel"
              >
                <Undo2 className="h-4 w-4 mr-2" />
                Cancel Snooze
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {snoozeOptions.map((option) => (
            <DropdownMenuItem
              key={option.label}
              onClick={(e) => {
                e.stopPropagation();
                onSnooze(option.getDate());
              }}
              data-testid={`snooze-${option.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setCustomDateOpen(true);
            }}
            data-testid="snooze-custom"
          >
            Custom date...
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customDateOpen} onOpenChange={setCustomDateOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Custom Snooze</DialogTitle>
            <DialogDescription>
              Choose when this item should reappear
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-col gap-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    {customDate ? format(customDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customDate}
                    onSelect={setCustomDate}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Time</Label>
              <Input
                type="time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                data-testid="input-snooze-time"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDateOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCustomSnooze}
              disabled={!customDate}
              data-testid="btn-confirm-snooze"
            >
              Snooze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface DismissModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  itemName: string;
  isPending?: boolean;
}

function DismissModal({ open, onClose, onConfirm, itemName, isPending }: DismissModalProps) {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Dismiss Item</DialogTitle>
          <DialogDescription>
            You are dismissing: <strong>{itemName}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="dismiss-reason">Reason (required)</Label>
          <Textarea
            id="dismiss-reason"
            placeholder="Please explain why you are dismissing this item..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-2"
            data-testid="input-dismiss-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!reason.trim() || isPending}
            data-testid="btn-confirm-dismiss"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DashboardSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  items: React.ReactNode[];
  emptyMessage: string;
  count: number;
  accentColor: string;
  linkTo: string;
}

function DashboardSection({ 
  title, 
  description, 
  icon, 
  items, 
  emptyMessage, 
  count,
  accentColor,
  linkTo 
}: DashboardSectionProps) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", accentColor)}>
              {icon}
            </div>
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                {title}
                {count > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {count}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <Link href={linkTo}>
            <span className="text-sm text-primary hover:underline flex items-center gap-1 cursor-pointer" data-testid={`link-view-all-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              View All <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {count === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {items}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ActionItemProps {
  href: string;
  primary: string;
  secondary: string;
  badge?: { label: string; className?: string };
  ageDays?: number;
  assignee?: string;
  showAssignee: boolean;
  testId: string;
  origin?: string;
  isSnoozed?: boolean;
  snoozedUntil?: string;
  onSnooze?: (snoozedUntil: Date) => void;
  onUnsnooze?: () => void;
  onDismiss?: () => void;
  canDismiss?: boolean;
  isPending?: boolean;
  onSendEmail?: () => void;
  sendEmailTitle?: string;
  isSendingEmail?: boolean;
}

function ActionItem({ 
  href, 
  primary, 
  secondary, 
  badge,
  ageDays,
  assignee, 
  showAssignee, 
  testId, 
  origin,
  isSnoozed,
  snoozedUntil,
  onSnooze,
  onUnsnooze,
  onDismiss,
  canDismiss,
  isPending,
  onSendEmail,
  sendEmailTitle,
  isSendingEmail,
}: ActionItemProps) {
  const hrefWithOrigin = origin ? `${href}?from=${origin}` : href;

  return (
    <div className="relative group">
      <Link href={hrefWithOrigin}>
        <div 
          className={cn(
            "flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer",
            isSnoozed && "opacity-60 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20"
          )}
          data-testid={testId}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{primary}</span>
              {badge && (
                <Badge className={cn("text-xs", badge.className)}>
                  {badge.label}
                </Badge>
              )}
              {ageDays !== undefined && (
                <span className="text-xs text-muted-foreground font-mono">{ageDays}d</span>
              )}
              {isSnoozed && snoozedUntil && (
                <Badge variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Snoozed until {formatSnoozedUntil(snoozedUntil)}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{secondary}</p>
            {showAssignee && assignee && (
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                <User className="w-3 h-3" />
                {assignee}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onSendEmail && (
              <div onClick={(e) => e.preventDefault()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  title={sendEmailTitle || 'Send email'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSendEmail();
                  }}
                  disabled={isPending || isSendingEmail}
                >
                  {isSendingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 text-blue-500" />
                  )}
                </Button>
              </div>
            )}
            {onSnooze && (
              <div onClick={(e) => e.preventDefault()}>
                <SnoozeDropdown 
                  onSnooze={onSnooze} 
                  onUnsnooze={onUnsnooze}
                  isSnoozed={isSnoozed}
                  snoozedUntil={snoozedUntil}
                  disabled={isPending} 
                />
              </div>
            )}
            {canDismiss && onDismiss && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDismiss();
                }}
                disabled={isPending}
                data-testid="btn-dismiss"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function Dashboard() {
  usePageTitle("Dashboard");
  const [showAll, setShowAll] = useState(false);
  const [includeSnoozed, setIncludeSnoozed] = useState(() => {
    return localStorage.getItem('dashboard-show-snoozed') === 'true';
  });
  const [dismissModal, setDismissModal] = useState<{
    open: boolean;
    itemType: ActionItemType;
    itemId: number;
    itemName: string;
  } | null>(null);
  const [lastDismissed, setLastDismissed] = useState<{
    id: number;
    itemType: ActionItemType;
    itemId: number;
    itemName: string;
  } | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const queryKey = `/api/dashboard?showAll=${showAll}&includeSnoozed=${includeSnoozed}`;

  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: [queryKey],
    refetchOnMount: 'always',
  });

  const snoozeMutation = useMutation({
    mutationFn: async ({ itemType, itemId, snoozedUntil }: { 
      itemType: ActionItemType; 
      itemId: number; 
      snoozedUntil: Date 
    }) => {
      return apiRequest('POST', '/api/action-item-statuses', {
        itemType,
        itemId,
        status: 'snoozed',
        snoozedUntil: snoozedUntil.toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({
        title: "Item snoozed",
        description: "The item has been snoozed and will reappear later.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to snooze item. Please try again.",
        variant: "destructive",
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async ({ itemType, itemId, dismissReason }: { 
      itemType: ActionItemType; 
      itemId: number; 
      dismissReason: string 
    }) => {
      const response = await apiRequest('POST', '/api/action-item-statuses', {
        itemType,
        itemId,
        status: 'dismissed',
        dismissReason,
      });
      return response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setDismissModal(null);
      
      // Store for undo
      const dismissed = {
        id: (data as any).id,
        itemType: variables.itemType,
        itemId: variables.itemId,
        itemName: dismissModal?.itemName || 'Item',
      };
      setLastDismissed(dismissed);
      
      // Clear any existing undo timeout
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
      
      // Show undo toast
      toast({
        title: "Item dismissed",
        description: (
          <div className="flex items-center gap-2">
            <span>Press Ctrl+Z to undo</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleUndo(dismissed)}
              className="h-6"
            >
              <Undo2 className="h-3 w-3 mr-1" />
              Undo
            </Button>
          </div>
        ),
        duration: 10000,
      });
      
      // Clear undo option after 10 seconds
      undoTimeoutRef.current = setTimeout(() => {
        setLastDismissed(null);
      }, 10000);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to dismiss item. Please try again.",
        variant: "destructive",
      });
    },
  });

  const undoMutation = useMutation({
    mutationFn: async (statusId: number) => {
      return apiRequest('DELETE', `/api/action-item-statuses/${statusId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setLastDismissed(null);
      toast({
        title: "Undo successful",
        description: "The item has been restored.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to undo. Please try again.",
        variant: "destructive",
      });
    },
  });

  const unsnoozeMutation = useMutation({
    mutationFn: async ({ itemType, itemId }: { itemType: ActionItemType; itemId: number }) => {
      // Find the status ID from actionItemStatuses
      const status = data?.actionItemStatuses.find(
        s => s.itemType === itemType && s.itemId === itemId && s.status === 'snoozed'
      );
      if (!status) {
        throw new Error('Snooze status not found');
      }
      return apiRequest('DELETE', `/api/action-item-statuses/${status.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({
        title: "Snooze cancelled",
        description: "The item is now visible in your dashboard.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to cancel snooze. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleUndo = useCallback((dismissed: typeof lastDismissed) => {
    if (dismissed) {
      undoMutation.mutate(dismissed.id);
    }
  }, [undoMutation]);

  const handleUnsnooze = (itemType: ActionItemType, itemId: number) => () => {
    unsnoozeMutation.mutate({ itemType, itemId });
  };

  // Keyboard shortcut for undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && lastDismissed) {
        e.preventDefault();
        handleUndo(lastDismissed);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastDismissed, handleUndo]);

  // Persist includeSnoozed to localStorage
  useEffect(() => {
    localStorage.setItem('dashboard-show-snoozed', includeSnoozed.toString());
  }, [includeSnoozed]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const sendWatchlistEmailMutation = useMutation({
    mutationFn: ({ watchlistId, emailType }: { watchlistId: number; emailType: 'reminder' | 'dismissal' }) =>
      apiRequest('POST', `/api/watchlists/${watchlistId}/send-email`, { emailType }).then(r => r.json()),
    onSuccess: (result: any) => {
      const sent = result?.sentCount ?? 0;
      const blocked = result?.blockedCount ?? 0;
      if (sent > 0) {
        toast({ title: "Email sent", description: `Sent to ${sent} recipient(s).` });
      } else if (blocked > 0) {
        toast({ title: "Email blocked (test mode)", description: `Blocked for ${blocked} recipient(s) — domain not in allowed list.`, variant: "destructive" });
      } else {
        toast({ title: "No recipients", description: "No contacts with email addresses found on this work order.", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send watchlist email.", variant: "destructive" });
    },
  });

  const handleSnooze = (itemType: ActionItemType, itemId: number) => (snoozedUntil: Date) => {
    snoozeMutation.mutate({ itemType, itemId, snoozedUntil });
  };

  const handleDismiss = (itemType: ActionItemType, itemId: number, itemName: string) => () => {
    setDismissModal({ open: true, itemType, itemId, itemName });
  };

  const confirmDismiss = (reason: string) => {
    if (dismissModal) {
      dismissMutation.mutate({
        itemType: dismissModal.itemType,
        itemId: dismissModal.itemId,
        dismissReason: reason,
      });
    }
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="Dashboard" subtitle="Your action items at a glance">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ResponsiveLayout>
    );
  }

  if (error || !data) {
    return (
      <ResponsiveLayout title="Dashboard" subtitle="Your action items at a glance">
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          Failed to load dashboard data. Please try again.
        </div>
      </ResponsiveLayout>
    );
  }

  const showAssignee = data.isAdmin && showAll;
  const isPending = snoozeMutation.isPending || dismissMutation.isPending || unsnoozeMutation.isPending;

  const getAgeDays = (latestStatusChangedAt?: string | null): number => {
    if (!latestStatusChangedAt) return 0;
    const raw = latestStatusChangedAt;
    // Append 'Z' only if the string has no timezone indicator (Z, +HH:MM, or -HH:MM)
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
    const since = new Date(hasTimezone ? raw : raw + 'Z');
    if (isNaN(since.getTime())) return 0;
    return Math.max(0, Math.floor((Date.now() - since.getTime()) / 86_400_000));
  };

  const INTERNAL_STATUSES = ['new', 'validating', 'preparing_documents', 'work_complete'];
  const CLIENT_STATUSES = ['collecting_inputs', 'awaiting_payment', 'awaiting_signatures', 'awaiting_client_filing'];
  const AGENCY_STATUSES = ['submitted'];

  type OpenWorkOrder = DashboardData['openWorkOrders'][number];
  const stageEntryTime = (wo: OpenWorkOrder) => new Date(wo.latestStatusChangedAt ?? wo.createdAt).getTime();
  const byStageAge = (a: OpenWorkOrder, b: OpenWorkOrder) => stageEntryTime(a) - stageEntryTime(b);

  const internalActionWOs = data.openWorkOrders.filter(wo => INTERNAL_STATUSES.includes(wo.status)).sort(byStageAge);
  const clientActionWOs = data.openWorkOrders.filter(wo => CLIENT_STATUSES.includes(wo.status)).sort(byStageAge);
  const agencyActionWOs = data.openWorkOrders.filter(wo => AGENCY_STATUSES.includes(wo.status)).sort(byStageAge);

  const totalActionItems = data.totalCounts
    ? (data.totalCounts.openWorkOrders +
       data.totalCounts.expiredWatchlists +
       data.totalCounts.unsentLetters +
       data.totalCounts.unresolvedIncidents +
       data.totalCounts.failedPayments +
       data.totalCounts.unpaidInvoices +
       data.totalCounts.agedWatchlists)
    : (data.openWorkOrders.length +
       data.expiredWatchlists.length +
       data.unsentLetters.length +
       data.unresolvedIncidents.length +
       data.failedPayments.length +
       data.unpaidInvoices.length +
       (data.agedWatchlists?.length ?? 0));

  return (
    <ResponsiveLayout title="Dashboard" subtitle="Your action items at a glance">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="text-dashboard-title">Dashboard</h1>
            <p className="text-muted-foreground">
              {totalActionItems === 0 
                ? "All caught up! No pending action items."
                : `${totalActionItems} action item${totalActionItems !== 1 ? 's' : ''} need${totalActionItems === 1 ? 's' : ''} attention`}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch 
                id="show-snoozed" 
                checked={includeSnoozed} 
                onCheckedChange={setIncludeSnoozed}
                data-testid="switch-show-snoozed"
              />
              <Label htmlFor="show-snoozed" className="text-sm cursor-pointer">
                Show snoozed
              </Label>
            </div>
            {data.isAdmin && (
              <div className="flex items-center gap-2">
                <Switch 
                  id="show-all" 
                  checked={showAll} 
                  onCheckedChange={setShowAll}
                  data-testid="switch-show-all"
                />
                <Label htmlFor="show-all" className="text-sm cursor-pointer">
                  View all assignments
                </Label>
              </div>
            )}
          </div>
        </div>

        {/* Work Order Action Group Cards — top row, always 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Internal Action */}
          <DashboardSection
            title="Internal Action"
            description="Work orders awaiting staff action"
            icon={<FileText className="w-5 h-5 text-blue-600" />}
            accentColor="bg-blue-100 dark:bg-blue-900/30"
            count={data.openWorkOrderCounts?.internal ?? internalActionWOs.length}
            emptyMessage="No work orders need internal action"
            linkTo={`/work-orders?status=new,validating,preparing_documents,work_complete${showAll ? '&tab=status' : ''}`}
            items={internalActionWOs.slice(0, 10).map((wo) => (
              <ActionItem
                key={wo.id}
                href={`/work-orders/${wo.id}`}
                primary={wo.workOrderNumber}
                secondary={`${wo.companyName || 'No company'}${wo.serviceTypeName || wo.serviceType ? ` • ${wo.serviceTypeName || wo.serviceType}` : ''}`}
                badge={{ label: formatStatus(wo.status), className: statusColors[wo.status] }}
                ageDays={getAgeDays(wo.latestStatusChangedAt)}
                assignee={formatAssignee(wo.assignedFirstName, wo.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-work-order-internal-${wo.id}`}
                origin="dashboard"
                isSnoozed={wo.isSnoozed}
                snoozedUntil={wo.snoozedUntil}
                onSnooze={handleSnooze('work_order', wo.id)}
                onUnsnooze={wo.isSnoozed ? handleUnsnooze('work_order', wo.id) : undefined}
                isPending={isPending}
              />
            ))}
          />

          {/* Client Action */}
          <DashboardSection
            title="Client Action"
            description="Work orders awaiting client response"
            icon={<User className="w-5 h-5 text-amber-600" />}
            accentColor="bg-amber-100 dark:bg-amber-900/30"
            count={data.openWorkOrderCounts?.client ?? clientActionWOs.length}
            emptyMessage="No work orders need client action"
            linkTo={`/work-orders?status=collecting_inputs,awaiting_payment,awaiting_signatures,awaiting_client_filing${showAll ? '&tab=status' : ''}`}
            items={clientActionWOs.slice(0, 10).map((wo) => (
              <ActionItem
                key={wo.id}
                href={`/work-orders/${wo.id}`}
                primary={wo.workOrderNumber}
                secondary={`${wo.companyName || 'No company'}${wo.serviceTypeName || wo.serviceType ? ` • ${wo.serviceTypeName || wo.serviceType}` : ''}`}
                badge={{ label: formatStatus(wo.status), className: statusColors[wo.status] }}
                ageDays={getAgeDays(wo.latestStatusChangedAt)}
                assignee={formatAssignee(wo.assignedFirstName, wo.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-work-order-client-${wo.id}`}
                origin="dashboard"
                isSnoozed={wo.isSnoozed}
                snoozedUntil={wo.snoozedUntil}
                onSnooze={handleSnooze('work_order', wo.id)}
                onUnsnooze={wo.isSnoozed ? handleUnsnooze('work_order', wo.id) : undefined}
                isPending={isPending}
              />
            ))}
          />

          {/* Agency Action */}
          <DashboardSection
            title="Agency Action"
            description="Work orders submitted to external agencies"
            icon={<Globe className="w-5 h-5 text-purple-600" />}
            accentColor="bg-purple-100 dark:bg-purple-900/30"
            count={data.openWorkOrderCounts?.agency ?? agencyActionWOs.length}
            emptyMessage="No work orders with agencies"
            linkTo={`/work-orders?status=submitted${showAll ? '&tab=status' : ''}`}
            items={agencyActionWOs.slice(0, 10).map((wo) => (
              <ActionItem
                key={wo.id}
                href={`/work-orders/${wo.id}`}
                primary={wo.workOrderNumber}
                secondary={`${wo.companyName || 'No company'}${wo.serviceTypeName || wo.serviceType ? ` • ${wo.serviceTypeName || wo.serviceType}` : ''}`}
                badge={{ label: formatStatus(wo.status), className: statusColors[wo.status] }}
                ageDays={getAgeDays(wo.latestStatusChangedAt)}
                assignee={formatAssignee(wo.assignedFirstName, wo.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-work-order-agency-${wo.id}`}
                origin="dashboard"
                isSnoozed={wo.isSnoozed}
                snoozedUntil={wo.snoozedUntil}
                onSnooze={handleSnooze('work_order', wo.id)}
                onUnsnooze={wo.isSnoozed ? handleUnsnooze('work_order', wo.id) : undefined}
                isPending={isPending}
              />
            ))}
          />
        </div>

        {/* Remaining action sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">

          <DashboardSection
            title="Expired Watchlists"
            description="Watchlists with no letter matches"
            icon={<Eye className="w-5 h-5 text-amber-600" />}
            accentColor="bg-amber-100 dark:bg-amber-900/30"
            count={data.totalCounts?.expiredWatchlists ?? data.expiredWatchlists.length}
            emptyMessage="No expired watchlists"
            linkTo="/watchlists?status=expired&noMatches=true"
            items={data.expiredWatchlists.slice(0, 10).map((wl) => (
              <ActionItem
                key={wl.id}
                href={`/work-orders/${wl.workOrderId}`}
                primary={wl.targetLabel || wl.targetIdentifier}
                secondary={`${wl.targetType.toUpperCase()} ${wl.targetIdentifier} • ${wl.workOrderNumber || 'N/A'}`}
                badge={{ label: 'Expired', className: 'bg-amber-100 text-amber-800' }}
                assignee={formatAssignee(wl.assignedFirstName, wl.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-watchlist-${wl.id}`}
                origin="dashboard"
                isSnoozed={wl.isSnoozed}
                snoozedUntil={wl.snoozedUntil}
                onSnooze={handleSnooze('watchlist', wl.id)}
                onUnsnooze={wl.isSnoozed ? handleUnsnooze('watchlist', wl.id) : undefined}
                onDismiss={handleDismiss('watchlist', wl.id, wl.targetLabel || wl.targetIdentifier)}
                canDismiss={true}
                isPending={isPending}
                onSendEmail={() => sendWatchlistEmailMutation.mutate({ watchlistId: wl.id, emailType: 'dismissal' })}
                sendEmailTitle="Send dismissal email"
                isSendingEmail={sendWatchlistEmailMutation.isPending}
              />
            ))}
          />

          <DashboardSection
              title="Aged Watchlists"
              description="Active watchlists >60 days with no matches"
              icon={<Eye className="w-5 h-5 text-amber-600" />}
              accentColor="bg-amber-100 dark:bg-amber-900/30"
              count={data.totalCounts?.agedWatchlists ?? data.agedWatchlists?.length ?? 0}
              emptyMessage="No aged watchlists"
              linkTo="/watchlists?status=active&noMatches=true"
              items={(data.agedWatchlists ?? []).slice(0, 10).map((wl) => {
                const ageDays = wl.createdAt
                  ? Math.round((Date.now() - new Date(wl.createdAt).getTime()) / 86_400_000)
                  : 0;
                return (
                  <div key={wl.id} className="relative group">
                    <Link href={`/work-orders/${wl.workOrderId}`}>
                      <div
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer",
                          wl.isSnoozed && "opacity-60 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20"
                        )}
                        data-testid={`item-aged-watchlist-${wl.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">{wl.targetLabel || wl.targetIdentifier}</span>
                            <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                              {ageDays}d
                            </Badge>
                            {wl.isSnoozed && wl.snoozedUntil && (
                              <Badge variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Snoozed until {formatSnoozedUntil(wl.snoozedUntil)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {wl.targetType.toUpperCase()} {wl.targetIdentifier} • {wl.workOrderNumber || 'N/A'}
                          </p>
                          {showAssignee && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <User className="w-3 h-3" />
                              {formatAssignee(wl.assignedFirstName, wl.assignedLastName)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <div onClick={(e) => e.preventDefault()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Send reminder email"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                sendWatchlistEmailMutation.mutate({ watchlistId: wl.id, emailType: 'reminder' });
                              }}
                              disabled={sendWatchlistEmailMutation.isPending || isPending}
                              data-testid={`btn-aged-wl-email-${wl.id}`}
                            >
                              {sendWatchlistEmailMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4 text-teal-600" />
                              )}
                            </Button>
                          </div>
                          <div onClick={(e) => e.preventDefault()}>
                            <SnoozeDropdown
                              onSnooze={handleSnooze('watchlist', wl.id)}
                              onUnsnooze={wl.isSnoozed ? handleUnsnooze('watchlist', wl.id) : undefined}
                              isSnoozed={wl.isSnoozed}
                              snoozedUntil={wl.snoozedUntil}
                              disabled={isPending}
                            />
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            />

          <DashboardSection
            title="Unsent Authority Letters"
            description="Letters awaiting email delivery"
            icon={<Mail className="w-5 h-5 text-purple-600" />}
            accentColor="bg-purple-100 dark:bg-purple-900/30"
            count={data.totalCounts?.unsentLetters ?? data.unsentLetters.length}
            emptyMessage="All letters sent"
            linkTo="/authority-letters?emailStatus=unsent"
            items={data.unsentLetters.slice(0, 10).map((letter) => (
              <ActionItem
                key={letter.id}
                href={`/work-orders/${letter.workOrderId}`}
                primary={letter.docketFull || 'Unknown Docket'}
                secondary={`${letter.companyName || 'Unknown'} • ${letter.letterType || 'N/A'}`}
                badge={{ 
                  label: formatStatus(letter.emailStatus || 'pending'), 
                  className: statusColors[letter.emailStatus || 'pending'] 
                }}
                assignee={formatAssignee(letter.assignedFirstName, letter.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-letter-${letter.id}`}
                origin="dashboard"
                isSnoozed={letter.isSnoozed}
                snoozedUntil={letter.snoozedUntil}
                onSnooze={handleSnooze('authority_letter', letter.id)}
                onUnsnooze={letter.isSnoozed ? handleUnsnooze('authority_letter', letter.id) : undefined}
                onDismiss={handleDismiss('authority_letter', letter.id, letter.docketFull || 'Unknown Docket')}
                canDismiss={true}
                isPending={isPending}
              />
            ))}
          />

          <DashboardSection
            title="Unresolved FMCSA Incidents"
            description="Incidents requiring attention"
            icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
            accentColor="bg-red-100 dark:bg-red-900/30"
            count={data.totalCounts?.unresolvedIncidents ?? data.unresolvedIncidents.length}
            emptyMessage="No unresolved incidents"
            linkTo="/fmcsa-incidents?status=unresolved"
            items={data.unresolvedIncidents.slice(0, 10).map((incident) => (
              <ActionItem
                key={incident.id}
                href="/fmcsa-incidents"
                primary={incident.incidentNumber}
                secondary={`${incident.companyName || 'No company linked'} • ${new Date(incident.createdAt).toLocaleDateString()}`}
                badge={{ label: 'Unresolved', className: 'bg-red-100 text-red-800' }}
                assignee={formatAssignee(incident.createdByFirstName, incident.createdByLastName)}
                showAssignee={showAssignee}
                testId={`item-incident-${incident.id}`}
                isSnoozed={incident.isSnoozed}
                snoozedUntil={incident.snoozedUntil}
                onSnooze={handleSnooze('fmcsa_incident', incident.id)}
                onUnsnooze={incident.isSnoozed ? handleUnsnooze('fmcsa_incident', incident.id) : undefined}
                isPending={isPending}
              />
            ))}
          />

          <DashboardSection
            title="Failed Payments"
            description="Payments that could not be collected"
            icon={<CreditCard className="w-5 h-5 text-rose-600" />}
            accentColor="bg-rose-100 dark:bg-rose-900/30"
            count={data.totalCounts?.failedPayments ?? data.failedPayments.length}
            emptyMessage="No failed payments"
            linkTo="/payments?status=uncollectible,voided"
            items={data.failedPayments.slice(0, 10).map((payment) => (
              <ActionItem
                key={payment.id}
                href={`/work-orders/${payment.workOrderId}`}
                primary={payment.stripeInvoiceNumber || payment.workOrderNumber || 'Unknown'}
                secondary={`${payment.companyName || 'Unknown'} • ${formatCurrency(payment.amountDue)}`}
                badge={{ 
                  label: formatStatus(payment.status), 
                  className: statusColors[payment.status] 
                }}
                assignee={formatAssignee(payment.assignedFirstName, payment.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-failed-payment-${payment.id}`}
                origin="dashboard"
                isSnoozed={payment.isSnoozed}
                snoozedUntil={payment.snoozedUntil}
                onSnooze={handleSnooze('payment', payment.id)}
                onUnsnooze={payment.isSnoozed ? handleUnsnooze('payment', payment.id) : undefined}
                onDismiss={handleDismiss('payment', payment.id, payment.stripeInvoiceNumber || payment.workOrderNumber || 'Unknown')}
                canDismiss={true}
                isPending={isPending}
              />
            ))}
          />

          <DashboardSection
            title="Unpaid Invoices"
            description="Invoices awaiting payment"
            icon={<Receipt className="w-5 h-5 text-orange-600" />}
            accentColor="bg-orange-100 dark:bg-orange-900/30"
            count={data.totalCounts?.unpaidInvoices ?? data.unpaidInvoices.length}
            emptyMessage="All invoices paid"
            linkTo="/invoices?status=pending,partial,overdue"
            items={data.unpaidInvoices.slice(0, 10).map((invoice) => (
              <ActionItem
                key={invoice.id}
                href={`/work-orders/${invoice.workOrderId}`}
                primary={invoice.stripeInvoiceNumber || invoice.workOrderNumber || 'Unknown'}
                secondary={`${invoice.companyName || 'Unknown'} • ${formatCurrency(invoice.unpaidBalance || invoice.amountDue)}`}
                badge={{ 
                  label: formatStatus(invoice.status), 
                  className: statusColors[invoice.status] 
                }}
                assignee={formatAssignee(invoice.assignedFirstName, invoice.assignedLastName)}
                showAssignee={showAssignee}
                testId={`item-unpaid-invoice-${invoice.id}`}
                origin="dashboard"
                isSnoozed={invoice.isSnoozed}
                snoozedUntil={invoice.snoozedUntil}
                onSnooze={handleSnooze('invoice', invoice.id)}
                onUnsnooze={invoice.isSnoozed ? handleUnsnooze('invoice', invoice.id) : undefined}
                onDismiss={handleDismiss('invoice', invoice.id, invoice.stripeInvoiceNumber || invoice.workOrderNumber || 'Unknown')}
                canDismiss={true}
                isPending={isPending}
              />
            ))}
          />
        </div>
      </div>

      {dismissModal && (
        <DismissModal
          open={dismissModal.open}
          onClose={() => setDismissModal(null)}
          onConfirm={confirmDismiss}
          itemName={dismissModal.itemName}
          isPending={dismissMutation.isPending}
        />
      )}
    </ResponsiveLayout>
  );
}
